import React from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiCreditCard, FiStar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageHeading from './PageHeading';

const pricingPlans = [
  {
    name: 'Glint Sponsor',
    amount: 5,
    priceId: 'price_1T66LeIEnneB4h0mImv5wCmp',
    paymentLink: 'https://buy.stripe.com/bJecN6dTd4OF2zl12a0Fi0Y',
    type: 'one-time',
    popular: false
  },
  {
    name: 'Glint Sponsor',
    amount: 8,
    priceId: 'price_1T66LeIEnneB4h0m54bChJGe',
    paymentLink: 'https://buy.stripe.com/6oUeVe02na8ZgqbcKS0Fi0X',
    type: 'one-time',
    popular: true
  },
  {
    name: 'Glint Sponsor',
    amount: 10,
    priceId: 'price_1T66LeIEnneB4h0mhNCwYWl1',
    paymentLink: 'https://buy.stripe.com/28E4gAaH1a8Zc9VaCK0Fi10',
    type: 'one-time',
    popular: false
  },
  {
    name: 'Glint Sponsor',
    amount: 5,
    priceId: 'price_1T66LeIEnneB4h0mmqNWmGiE',
    paymentLink: 'https://buy.stripe.com/4gM5kEcP9epfb5R4em0Fi0Z',
    interval: 'month',
    type: 'subscription',
    popular: false
  },
  {
    name: 'Glint Sponsor',
    amount: 8,
    priceId: 'price_1T66LeIEnneB4h0mcZRgBIfH',
    paymentLink: 'https://buy.stripe.com/3cIfZi2av5SJddZdOW0Fi0W',
    interval: 'month',
    type: 'subscription',
    popular: true
  },
  {
    name: 'Glint Sponsor',
    amount: 10,
    priceId: 'price_1T66LeIEnneB4h0mgIL33OVD',
    paymentLink: 'https://buy.stripe.com/3cI9AUg1l94Vei37qy0Fi11',
    interval: 'month',
    type: 'subscription',
    popular: false
  }
];

const handlePayment = (paymentLink) => {
  window.open(paymentLink, '_blank', 'noopener,noreferrer');
};

const PricingCard = ({ plan, index, lang }) => (
  <motion.article
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.1 }}
    className={`relative bg-white rounded-3xl border-2 p-8 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-100 hover:-translate-y-2 ${
      plan.popular
        ? 'border-indigo-500 ring-4 ring-indigo-100 scale-105'
        : 'border-slate-200 hover:border-indigo-300'
    }`}
  >
    {plan.popular && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2">
        <div className="bg-indigo-600 text-white px-6 py-2 rounded-full text-sm font-black flex items-center gap-2 shadow-lg">
          <SafeIcon icon={FiStar} className="text-xs fill-current" />
          {lang === 'zh-TW' ? '最受歡迎' : 'Most Popular'}
        </div>
      </div>
    )}

    <div className="text-center mb-8">
      <h3 className="text-2xl font-black text-slate-800 mb-2">
        {plan.name}
      </h3>
      <div className="flex items-baseline justify-center gap-1">
        <span className="text-4xl font-black text-indigo-600">
          ${plan.amount}
        </span>
        <span className="text-slate-500 font-medium">
          {plan.interval
            ? `/${lang === 'zh-TW' ? '月' : 'month'}`
            : lang === 'zh-TW'
              ? '一次性'
              : 'one-time'}
        </span>
      </div>
    </div>

    <div className="space-y-4 mb-8">
      {[
        lang === 'zh-TW'
          ? '支持 Emoji Glint 發展'
          : 'Support Emoji Glint development',
        lang === 'zh-TW'
          ? '無限制使用所有功能'
          : 'Unlimited access to all features',
        lang === 'zh-TW'
          ? '優先獲得新功能'
          : 'Early access to new features'
      ].map((feature) => (
        <div key={feature} className="flex items-center gap-3">
          <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
            <SafeIcon
              icon={FiCheck}
              className="text-indigo-600 text-xs stroke-[3]"
            />
          </div>
          <span className="text-slate-600">{feature}</span>
        </div>
      ))}

      {plan.interval && (
        <div className="flex items-center gap-3">
          <div className="w-5 h-5 bg-indigo-100 rounded-full flex items-center justify-center">
            <SafeIcon
              icon={FiCheck}
              className="text-indigo-600 text-xs stroke-[3]"
            />
          </div>
          <span className="text-slate-600">
            {lang === 'zh-TW' ? '隨時可取消' : 'Cancel anytime'}
          </span>
        </div>
      )}
    </div>

    <button
      onClick={() => handlePayment(plan.paymentLink)}
      className={`w-full py-4 px-6 rounded-2xl font-black text-lg transition-all duration-300 flex items-center justify-center gap-3 active:scale-95 ${
        plan.popular
          ? 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-lg shadow-indigo-200'
          : 'bg-slate-100 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 border-2 border-transparent hover:border-indigo-200'
      }`}
    >
      <SafeIcon icon={FiCreditCard} className="text-xl" />
      {lang === 'zh-TW' ? '立即贊助' : 'Sponsor now'}
    </button>
  </motion.article>
);

const PricingSection = ({ lang }) => {
  const oneTimePlans = pricingPlans.filter(
    (plan) => plan.type === 'one-time'
  );
  const subscriptionPlans = pricingPlans.filter(
    (plan) => plan.type === 'subscription'
  );

  return (
    <article className="py-20 px-6 bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        <header className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center gap-3 mb-6"
          >
            <div className="w-12 h-1 bg-indigo-500 rounded-full" />
            <span className="text-indigo-600 font-black uppercase tracking-widest text-sm">
              {lang === 'zh-TW' ? '支持我們' : 'Support us'}
            </span>
            <div className="w-12 h-1 bg-indigo-500 rounded-full" />
          </motion.div>

          <PageHeading className="text-5xl md:text-6xl font-black text-slate-800 mb-6 tracking-tighter">
            {lang === 'zh-TW'
              ? '如何支持 Emoji Glint？'
              : 'How can you support Emoji Glint?'}
          </PageHeading>

          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {lang === 'zh-TW'
              ? '您的支持讓我們能夠持續改進 Emoji Glint，為創作者提供更好的文案工具體驗。'
              : 'Your support helps us improve Emoji Glint and provide better tools for creators.'}
          </p>

          <p className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">
            {lang === 'zh-TW' ? '最近更新：' : 'Last updated: '}
            <time dateTime="2025-01-20">
              {lang === 'zh-TW' ? '2025 年 1 月 20 日' : '20 January 2025'}
            </time>
          </p>
        </header>

        <section className="mb-16" aria-labelledby="one-time-heading">
          <h2
            id="one-time-heading"
            className="text-3xl font-black text-slate-800 text-center mb-8"
          >
            {lang === 'zh-TW'
              ? '如何進行一次性贊助？'
              : 'How can you make a one-time contribution?'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {oneTimePlans.map((plan, index) => (
              <PricingCard
                key={plan.priceId}
                plan={plan}
                index={index}
                lang={lang}
              />
            ))}
          </div>
        </section>

        <section aria-labelledby="monthly-heading">
          <h2
            id="monthly-heading"
            className="text-3xl font-black text-slate-800 text-center mb-8"
          >
            {lang === 'zh-TW'
              ? '如何持續每月支持我們？'
              : 'How can you support us every month?'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {subscriptionPlans.map((plan, index) => (
              <PricingCard
                key={plan.priceId}
                plan={plan}
                index={index}
                lang={lang}
              />
            ))}
          </div>
        </section>

        <footer className="text-center mt-16">
          <p className="text-slate-500 text-sm max-w-2xl mx-auto">
            {lang === 'zh-TW'
              ? '所有付款均通過 Stripe 安全處理。贊助完全自願，無論是否贊助，您都可以繼續免費使用 Emoji Glint 的所有功能。'
              : 'All payments are securely processed through Stripe. Sponsorship is voluntary, and every Emoji Glint feature remains free.'}
          </p>
        </footer>
      </div>
    </article>
  );
};

export default PricingSection;